import { Box, Button, Grid, Link, Stack, TextField, Typography, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useOfflineDetectContext } from "../../../contexts/offline-detect-context/UseOfflineDetectContext";
import { useUserContext } from '../../../contexts/user-context/UserContext';
//import FormProperty from '../../forms/form-property/FormProperty';
import { AppNotificationTypeEnum } from "../../../contexts/app-notification-context/AppNotificationContextTypes";
import { useAppNotificationContext } from "../../../contexts/app-notification-context/UseAppNotificationContext";
import { RestError } from "../../../services/rest-service/RestService";
import { useConfirmation } from '../../../contexts/confirmation-context/ConfirmationContext';
import { useQueryNotifications } from '../../../queries/UseQueryNotifications';
import { useUserSettings } from '../../../queries/user-settings/UseUserSettings';
import htmlParse from 'html-react-parser';
import { TimeframeTypeEnum } from '../../../common/enums/TimeframeTypeEnum';
import { getTimeframeTimeDisplayText } from '../../../common/utilities/TimeframeUtilities';
import ActionMenu from '../../action-menu/ActionMenu';
import { useCallback, useMemo } from 'react';
import { ActionMenuItem } from '../../action-menu/ActionMenuItem';
//import FormPropertySection from '../../forms/form-property-section/FormPropertySection';
import { ApiKeyTypeEnum, IApiKeyModel } from '../../../services/user-data-service/Models';
import UserDataService from '../../../services/user-data-service/UserDataService';
import useNavigateTo, { NavigateToOptions } from '../../../hooks/use-navigate-to/useNavigateTo';
import { NewViewTypeEnum } from '../../../common/enums/NewViewTypeEnum';
//import { isMobile, isTablet } from 'react-device-detect';


export type ApiManagementProps = {
    visible: boolean,
};

enum ApiActionEnum  {
    Copy = 'copy',
    Regenerate = 'regenerate',
    Renew = 'renew',
    Revoke = 'revoke'
}

export default function ApiManagement({ visible }: ApiManagementProps) {

    //const isPhone = isMobile && !isTablet;
    
    const [userState] = useUserContext();
    const [,setAppNotificationState] = useAppNotificationContext();
    const [offlineState] = useOfflineDetectContext();
    const { acknowledge } = useConfirmation();

    const { data: userSettings, refetch } = useQueryNotifications(useUserSettings)({
        enabled: !offlineState.offline
    });

    const { t } = useTranslation();
    const theme = useTheme();

    const navigateTo = useNavigateTo();


    const primaryKeyExpired = userSettings?.primaryAPIKeyExpiration ? 
        new Date(userSettings?.primaryAPIKeyExpiration).getTime() < new Date().getTime() : 
        false;

    const onAction = useCallback(async (action: ApiActionEnum, keyType: ApiKeyTypeEnum) =>  {

        if (userSettings) {
            switch (action) {
                case ApiActionEnum.Copy: {

                    const value = keyType === ApiKeyTypeEnum.Primary && userSettings.primaryAPIKey ?
                        userSettings.primaryAPIKey :
                        keyType === ApiKeyTypeEnum.Secondary && userSettings.secondaryAPIKey ?
                        userSettings.secondaryAPIKey :
                        undefined;

                    if (value) {
                        await navigator.clipboard.writeText(value);

                        acknowledge(t(keyType === ApiKeyTypeEnum.Primary ?
                            'apiManagement.primaryKeyCopied' : 
                            'apiManagement.secondaryKeyCopied'));

                    }
                }

                    break;

                case ApiActionEnum.Regenerate: 

                    try {
              
                        acknowledge(t(keyType === ApiKeyTypeEnum.Primary ?
                                        'apiManagement.regeneratePrimaryKeySent' : 
                                        'apiManagement.regenerateSecondaryKeySent'));

                        const keyModel: IApiKeyModel = {
                            keyType,
                            primaryKey: keyType === ApiKeyTypeEnum.Primary ? 
                                            userSettings?.primaryAPIKey : 
                                            undefined,
                            secondaryKey: keyType === ApiKeyTypeEnum.Secondary ? 
                                            userSettings?.secondaryAPIKey : 
                                            undefined
                        };

                        await UserDataService.regenerateAPIKey(userState.user?.id as number, keyModel);

                        await refetch();

                        acknowledge(t(keyType === ApiKeyTypeEnum.Primary ?
                            'apiManagement.regeneratePrimaryKeyUpdated' : 
                            'apiManagement.regenerateSecondaryKeyUpdated'));


                    } catch (error) {
                        const restError = error as RestError;
                        
                        setAppNotificationState({
                            notificationType: AppNotificationTypeEnum.Error,
                            show: true,
                            error: restError
                        });
                    }


                    break;

                    case ApiActionEnum.Renew:
                        try {

                            const keyModel: IApiKeyModel = {
                                keyType,
                                primaryKey: keyType === ApiKeyTypeEnum.Primary ? 
                                                userSettings?.primaryAPIKey : 
                                                undefined,
                                secondaryKey: keyType === ApiKeyTypeEnum.Secondary ? 
                                                userSettings?.secondaryAPIKey : 
                                                undefined
                            };

                            await UserDataService.renewAPIKey(userState.user?.id as number, keyModel);

                            await refetch();

                            acknowledge(t(keyType === ApiKeyTypeEnum.Primary ?
                                'apiManagement.renewPrimaryKeySuccess' : 
                                'apiManagement.renewSecondaryKeySuccess'));

                        } catch (error) {
                            const restError = error as RestError;
                        
                            setAppNotificationState({
                                notificationType: AppNotificationTypeEnum.Error,
                                show: true,
                                error: restError
                            });
                        }

                        break;


                    case ApiActionEnum.Revoke:
                        try {

                            const keyModel: IApiKeyModel = {
                                keyType,
                            };

                            await UserDataService.revokeAPIKey(userState.user?.id as number, keyModel);

                            await refetch();

                            acknowledge(t(keyType === ApiKeyTypeEnum.Primary ?
                                'apiManagement.removePrimaryKeySuccess' : 
                                'apiManagement.removeSecondaryKeySuccess'));

                        } catch (error) {
                            const restError = error as RestError;
                        
                            setAppNotificationState({
                                notificationType: AppNotificationTypeEnum.Error,
                                show: true,
                                error: restError
                            });
                        }

                        break;

                    default:
                        break;
            }
        }
    },[
        acknowledge,
        t,
        setAppNotificationState,
        refetch,
        userSettings,
        userState?.user?.id
    ]);

    const primaryKeyActionItems: ActionMenuItem[] = useMemo(() => {

        const actions: ActionMenuItem[] = [{
            label: t("apiManagement.copy"),
            action: ApiActionEnum.Copy,
            onClick: () => {onAction(ApiActionEnum.Copy, ApiKeyTypeEnum.Primary)}, 
            order: 0
        }, {
            label: t("apiManagement.regenerate"),
            action: ApiActionEnum.Regenerate,
            onClick: () => {onAction(ApiActionEnum.Regenerate, ApiKeyTypeEnum.Primary)}, 
            order: 1
        },{
            label: t("apiManagement.renew"),
            action: ApiActionEnum.Renew,
            disabled: primaryKeyExpired,
            onClick: () => {onAction(ApiActionEnum.Renew, ApiKeyTypeEnum.Primary)}, 
           order: 2

        }, {
            label: t("apiManagement.revoke"),
            action: ApiActionEnum.Revoke,
            onClick: () => {onAction(ApiActionEnum.Revoke, ApiKeyTypeEnum.Primary)}, 
            order: 3
        }];

        return actions;
    }, [
        t,
        onAction,
        primaryKeyExpired
    ]);

    const secondaryKeyActionItems: ActionMenuItem[] = useMemo(() => {

        const actions: ActionMenuItem[] = [{
            label: t("apiManagement.copy"),
            action: ApiActionEnum.Copy,
            onClick: () => {onAction(ApiActionEnum.Copy, ApiKeyTypeEnum.Secondary)}, 
            order: 0
        }, {
            label: t("apiManagement.regenerate"),
            action: ApiActionEnum.Regenerate,
            onClick: () => {onAction(ApiActionEnum.Regenerate, ApiKeyTypeEnum.Secondary)}, 
            order: 1
        },{
            label: t("apiManagement.renew"),
            action: ApiActionEnum.Renew,
            disabled: primaryKeyExpired,
            onClick: () => {onAction(ApiActionEnum.Renew, ApiKeyTypeEnum.Secondary)}, 
            order: 2

        }, {
            label: t("apiManagement.revoke"),
            action: ApiActionEnum.Revoke,
            onClick: () => {onAction(ApiActionEnum.Revoke, ApiKeyTypeEnum.Secondary)}, 
            order: 3
        }];

        return actions;
    }, [
        t,
        onAction,
        primaryKeyExpired
    ]);


    async function onRequestApiAccess() {

        try {

            await UserDataService.requestAPIKey(userState.user?.id as number);

            await refetch();

            acknowledge(t('apiManagement.APIAccessGranted'));

        } catch (error) {
            const restError = error as RestError;
        
            setAppNotificationState({
                notificationType: AppNotificationTypeEnum.Error,
                show: true,
                error: restError
            });
        }        
    }

    async function onRemoveApiAccess() {
        try {

            const keyModel: IApiKeyModel = {
                keyType: ApiKeyTypeEnum.Primary | ApiKeyTypeEnum.Secondary
            };

            await UserDataService.revokeAPIKey(userState.user?.id as number, keyModel);

            await refetch();

            acknowledge(t('apiManagement.removeAPIAccessSuccess'));

        } catch (error) {
            const restError = error as RestError;
        
            setAppNotificationState({
                notificationType: AppNotificationTypeEnum.Error,
                show: true,
                error: restError
            });
        }
    }

    function onGoToPortal() {
        
        const options: NavigateToOptions = {
            newViewType: NewViewTypeEnum.NewTab
        }
    
        navigateTo(userSettings!.devPortalURL!, options);        
    }

    const hasApiKey = userSettings?.primaryAPIKey || userSettings?.secondaryAPIKey ? true : false;

    return visible ?
        <Stack direction='column' sx={{
            padding: theme.spacing(1)
        }}>

            {!hasApiKey ?
                <>

                    <Typography variant='body2'>
                        {htmlParse(t('apiManagement.apiAccessNotConfigured'))}
                    </Typography>

                    {/* <Button color='primary' */}
                    <Button color='inherit'
                        variant='contained'
                        sx={{
                            marginTop: theme.spacing(2),
                            marginLeft: 0,
                            marginRight: 'auto'
                        }}
                        onClick={() => onRequestApiAccess() }>
                        {t('apiManagement.requestAPIAccess')}
                    </Button>

                </>

            :

            <>

                <Typography variant='body2' sx={{
                    marginBottom: theme.spacing(2)
                }}>
                    {htmlParse(t('apiManagement.apiAccessConfigured'))}
                </Typography>


                <Grid container spacing={2}>
                    
                    <Grid size={{ xs: 12, sm: 6 }}>


                        <Stack direction='row'>

                            <TextField
                                variant="filled"
                                aria-label={t('apiManagement.primaryKey') as string}
                                label={t('apiManagement.primaryKey')}
                                value={userSettings?.primaryAPIKey ?? ''}
                                sx={{
                                    marginTop: theme.spacing(1),
                                    //width: isPhone ? '100%' : '500px',
                                    width: '100%',
                                    //maxWidth: 350
                                }}
                                helperText={t(!primaryKeyExpired ? 'apiManagement.primaryExpiresOn' : 'apiManagement.primaryExpiredOn', {
                                                ts: getTimeframeTimeDisplayText(TimeframeTypeEnum.None, userSettings?.primaryAPIKeyExpiration ?? '')
                                            })}
                                error={primaryKeyExpired}
                                slotProps={{
                                    input: {
                                        readOnly: true
                                    },

                                    inputLabel: {
                                        shrink: true
                                    }
                                }} />

                            <Box sx={{
                                marginTop: theme.spacing(1),

                            }}>
                                <ActionMenu actionItems={primaryKeyActionItems} />
                            </Box>

                        </Stack>
                </Grid>

                {
                userSettings?.secondaryAPIKey && 

                <Grid size={{ xs: 12, sm: 6 }}>
                        <Stack direction='row'>

                        <TextField
                            variant="filled"
                            aria-label={t('apiManagement.secondaryKey') as string}
                            label={t('apiManagement.secondaryKey')}
                            value={userSettings?.secondaryAPIKey ?? ''}
                            sx={{
                                marginTop: theme.spacing(1),
                                width: '100%',
                                //width: isPhone ? '100%' : '500px',
                                //maxWidth: 350
                            }}
                            helperText={!primaryKeyExpired ? t('apiManagement.secondaryExpiresOn', {
                                            ts: getTimeframeTimeDisplayText(TimeframeTypeEnum.None, userSettings?.secondaryAPIKeyExpiration ?? '')
                                        }) : 
                                        undefined}
                            slotProps={{
                                input: {
                                    readOnly: true
                                },

                                inputLabel: {
                                    shrink: true
                                }
                            }} />
                        <Box sx={{
                            marginTop: theme.spacing(1),
                        }}>
                            <ActionMenu actionItems={secondaryKeyActionItems} />
                        </Box>

                    </Stack>


                </Grid>



            }
            </Grid>


            <Button 
                    variant='contained'
                    color='primary'
                    sx={{
                        marginRight: 'auto'
                    }}
                    onClick={() =>  onRemoveApiAccess() }>
                
                    {t('apiManagement.removeAPIAccess')}
            </Button>


            </>

            }

            <Typography
                variant='body1'
                sx={{
                    marginLeft: theme.spacing(1),
                    marginTop: theme.spacing(3)
                }}>
                
                To view documentation or get support for our API, please visit our&nbsp;
                {/* <Link aria-label="developer portal" color="textPrimary" href={userSettings?.devPortalURL} target="_blank" variant='inherit' sx={{
                    fontWeight: 600
                }}>
                    Developer portal.
                </Link> */}
                <Button color='inherit'
                        variant='contained'
                        // sx={{
                        //     marginTop: theme.spacing(2),
                        //     marginLeft: 0,
                        //     marginRight: 'auto'
                        // }}
                        onClick={() => onGoToPortal() }>
                        Developer portal
                    </Button>

            </Typography>


        </Stack>
        : null;
}
